import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Badge } from "../../../components/ui/badge";
import { Progress } from "../../../components/ui/progress";
import { Button } from "../../../components/ui/button";
import { ChevronDown, ChevronRight, Clock, Pencil, Zap } from "lucide-react";
import { ROLE_CONFIG } from "./credit-data";
import { cn } from "lib/utils";
import localization from "services/localization";
import { useThemeStore } from "../../../context/useThemeStore";
import { ClipLoader } from "react-spinners";
import SpecificAPI from "services/SpecificApi";
import { useUserStore } from "context/useUserStore";
import { useLanguageStore } from "context/useLanguageStore";

export function UserCreditTable({ users, onEditUser }) {
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [usageHistory, setUsageHistory] = useState([]);
  const { userData, appUser } = useUserStore();
  const { theme } = useThemeStore();
  const { currentLang } = useLanguageStore();

  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 10;

  const toggleExpand = (userId) => {
    setExpandedUserId((prev) => (prev === userId ? null : userId));
    loadUsageHistory(userId);
  };

  const loadUsageHistory = (userId) => {
    setIsHistoryLoading(true);
    setHistoryPage(1);
    SpecificAPI.getAllHistory({ user_id: userId, status: "expired" }, userData?.selected_school_id)
      .then((res) => {
        var usageHistory = res
          ?.filter((h) => h.status == "expired")
          ?.map((h) => {
            return {
              id: h.id,
              date: h.created_at,
              action: h?.prompt?.title[currentLang] || h?.prompt?.title?.It || h?.prompt?.title?.En || "Unnamed Prompt",
              creditsUsed: (h.member_cost ?? 0) + (h.owner_cost ?? 0) || 0,
              description: h.title,
            };
          });
        setUsageHistory(usageHistory);
        setIsHistoryLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsHistoryLoading(false);
      });
  };

  const totalHistoryPages = Math.ceil(usageHistory.length / HISTORY_PAGE_SIZE);

  const paginatedHistory = usageHistory.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE);

  const getUsagePercent = (used, allocated) => {
    if (allocated === 0) return 0;
    return Math.round((used / allocated) * 100);
  };

  const getUsageColor = (percent) => {
    if (percent >= 90) return "text-red-600";
    if (percent >= 70) return "text-amber-600";
    return "text-emerald-600";
  };

  const getProgressColor = (percent) => {
    if (percent >= 90) return "bg-red-500";
    if (percent >= 70) return "bg-amber-500";
    return "bg-emerald-500";
  };

  const getInitials = (name) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  const select_bg = theme === "dark" ? "bg-[var(--card-bg)] text-white" : "bg-white text-foreground";

  return (
    <div className={"rounded-lg border " + select_bg}>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className="w-10" />
            <TableHead>{localization.user || "User"}</TableHead>
            <TableHead>{localization.role || "Role"}</TableHead>
            <TableHead className="text-right">{localization.allocated || "Allocated"}</TableHead>
            <TableHead className="text-right">{localization.used || "Used"}</TableHead>
            <TableHead className="text-right">{localization.remaining || "Remaining"}</TableHead>
            <TableHead className="w-[180px]">{localization.usage || "Usage"}</TableHead>
            <TableHead className="w-16 text-center">{localization.actions || "Actions"}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                {localization.no_users_found || "No users found."}
              </TableCell>
            </TableRow>
          )}
          {users
            .filter((u) => u?.school_id?.includes(userData?.selected_school_id))
            .map((user) => {
              const isExpanded = expandedUserId === user.id;
              const usagePct = getUsagePercent(user.used, user.allocated);

              return (
                <>
                  <TableRow
                    key={user.id}
                    className={cn("cursor-pointer transition-colors", isExpanded && "bg-muted/30")}
                    onClick={() => toggleExpand(user.id)}
                  >
                    <TableCell className="pl-4">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-primary-foreground"
                          style={{
                            backgroundColor: ROLE_CONFIG[user.role]?.color || "hsl(var(--primary))",
                          }}
                        >
                          {getInitials(user.name)}
                        </div>
                        <div>
                          <p className="font-medium leading-none">{user.name}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          "font-medium",
                          ROLE_CONFIG[user.role]?.bgClass,
                          ROLE_CONFIG[user.role]?.textClass,
                        )}
                      >
                        {ROLE_CONFIG[user.role]?.label || user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {user.allocated.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{user.used.toLocaleString()}</TableCell>
                    <TableCell className={cn("text-right font-semibold tabular-nums", getUsageColor(usagePct))}>
                      {user.remaining.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={usagePct} colorClass={getProgressColor(usagePct)} className="h-2 flex-1" />
                        <span
                          className={cn("w-10 text-right text-xs font-semibold tabular-nums", getUsageColor(usagePct))}
                        >
                          {usagePct}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditUser?.(user);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        <span className="sr-only">
                          {localization.edit_allocation_for || "Edit allocation for"} {user.name}
                        </span>
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Usage History */}
                  {isExpanded && !isHistoryLoading ? (
                    <TableRow key={`${user.id}-history`} className="bg-muted/20 hover:bg-muted/20">
                      <TableCell colSpan={8} className="p-0">
                        <div className="px-6 py-4">
                          <div className="mb-3 flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <h4 className="text-sm font-semibold">{localization.usage_history || "Usage History"}</h4>
                            <Badge variant="outline" className="text-xs">
                              {usageHistory.length} {localization.records || "records"}
                            </Badge>
                          </div>
                          <div className="overflow-hidden rounded-md border bg-background">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/50 hover:bg-muted/50">
                                  <TableHead className="text-xs">{localization.date || "Date"}</TableHead>
                                  <TableHead className="text-xs">{localization.prompt || "Prompt"}</TableHead>
                                  <TableHead className="text-xs">{localization.description || "Description"}</TableHead>
                                  <TableHead className="text-right text-xs">
                                    {localization.credits || "Credits"}
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {paginatedHistory.map((record) => (
                                  <TableRow key={record.id}>
                                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                                      {new Date(record.date).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })}
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-1.5">
                                        <Zap className="h-3 w-3 text-amber-500" />
                                        <span className="text-xs font-medium">{record.action}</span>
                                      </div>
                                    </TableCell>
                                    <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground">
                                      {record.description}
                                    </TableCell>
                                    <TableCell className="text-right text-xs font-semibold tabular-nums text-red-600">
                                      -{record.creditsUsed.toLocaleString()}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-xs text-muted-foreground">
                              Page {historyPage} of {totalHistoryPages || 1}
                            </span>

                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={historyPage === 1}
                                onClick={() => setHistoryPage((p) => p - 1)}
                              >
                                {localization.previous || "Previous"}
                              </Button>

                              <Button
                                size="sm"
                                variant="outline"
                                disabled={historyPage === totalHistoryPages || totalHistoryPages === 0}
                                onClick={() => setHistoryPage((p) => p + 1)}
                              >
                                {localization.next || "Next"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : isExpanded && isHistoryLoading ? (
                    <TableRow className="bg-muted/20 hover:bg-muted/20">
                      <TableCell colSpan={8}>
                        <div className="flex flex-col items-center justify-center h-40 gap-2">
                          <ClipLoader size={40} color="var(--primary-icon)" />
                          <span className="text-sm text-muted-foreground">{localization.loading || "Loading..."}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </>
              );
            })}
        </TableBody>
      </Table>
    </div>
  );
}
